/**
 * CalendarMonth Component
 *
 * Single month grid (7-col, Sun-Sat) for the date range picker.
 */

import React from 'react';
import { CalendarDayCell } from './CalendarDayCell';
import type { DayStatus } from './useDateRangePicker';

interface CalendarMonthProps {
  year: number;
  month: number; // 0-indexed
  getDayStatus: (date: Date) => DayStatus;
  onDayClick: (date: Date) => void;
  onDayHover: (date: Date | null) => void;
}

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getMonthDays(year: number, month: number): (Date | null)[][] {
  const firstDay = new Date(year, month, 1);
  const startDayOfWeek = firstDay.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weeks: (Date | null)[][] = [];
  let currentWeek: (Date | null)[] = [];

  // Leading empty cells
  for (let i = 0; i < startDayOfWeek; i++) {
    currentWeek.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(new Date(year, month, day));
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Trailing empty cells
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return weeks;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const CalendarMonth: React.FC<CalendarMonthProps> = ({
  year,
  month,
  getDayStatus,
  onDayClick,
  onDayHover,
}) => {
  const weeks = getMonthDays(year, month);
  const monthName = MONTH_NAMES[month];

  return (
    <div>
      <h3 className="text-center font-semibold text-gray-900 mb-4">
        {monthName} {year}
      </h3>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {DAY_LABELS.map((label) => (
              <th
                key={label}
                className="text-xs font-medium text-gray-500 text-center pb-2 w-[14.28%]"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIdx) => (
            <tr key={weekIdx}>
              {week.map((date, dayIdx) =>
                date ? (
                  <CalendarDayCell
                    key={date.getTime()}
                    date={date}
                    status={getDayStatus(date)}
                    onClick={onDayClick}
                    onHover={onDayHover}
                  />
                ) : (
                  <td key={`empty-${dayIdx}`} className="p-0" />
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CalendarMonth;
